import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  TextareaAutosize,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import makeStyles from '@mui/styles/makeStyles';

import clsx from 'clsx';
import { Formik } from 'formik';
import React from 'react';
import { useRecoilStateLoadable } from 'recoil';
import * as Yup from 'yup';
import { useAddNpsScoreMutation } from '../../../api/apigQuery';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import { toAbsoluteUrl } from '../../../_metronic/_helpers';
import { ModalWithStyles } from './ModalWithStyles';

const useStyles = makeStyles((theme: Theme) => ({
  npsModal: {
    '& .MuiPaper-root.MuiDialog-paper': {
      borderRadius: '5px',
      maxWidth: '760px',
      top: '100px',
      overflow: 'visible',
    },
  },
  centerDisplay: {
    display: 'flex',
    justifyContent: 'center',
  },
  title: {
    fontSize: '22px',
    fontWeight: 600,
    margin: '0 0 20px',
  },
  subtitle: {
    fontSize: '14px',
    fontWeight: 600,
    textAlign: 'left',
    margin: '16px 0px',
    width: '100%',
  },
  modalContainer: {
    padding: '0px 24px',
  },
  closeButton: {},
  closeButtonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    height: '48px',
  },
  kixieIconContainer: {
    padding: '28px',
    width: '128px',
    height: '128px',
    position: 'relative',
    top: '-84px',
    display: 'flex',
    zIndex: 1301,
    borderRadius: '50%',
    backgroundColor:
      theme.palette.mode === 'light' ? 'white' : 'rgb(66, 66, 66)',
    justifyContent: 'center',
  },
  scoreButtons: {
    borderRadius: '5px',
  },
  scoreBox: {
    padding: 0,
  },
  score: {
    borderColor: '#ccc',
    fontSize: '16px',
    fontWeight: 600,
    padding: '10px 20px',
    '&:first-child': {
      color: '#f10606',
      borderTopLeftRadius: '5px',
      borderBottomLeftRadius: '5px',
    },
    '&:nth-of-type(2)': {
      color: '#f10606',
    },
    '&:nth-of-type(3)': {
      color: '#f10606',
    },
    '&:nth-of-type(4)': {
      color: '#f10606',
    },
    '&:nth-of-type(5)': {
      color: '#f10606',
    },
    '&:nth-of-type(6)': {
      color: '#f10606',
    },
    '&:nth-of-type(7)': {
      color: '#f10606',
    },
    '&:nth-of-type(8)': {
      color: '#F98330',
    },
    '&:nth-of-type(9)': {
      color: '#F98330',
    },
    '&:nth-of-type(10)': {
      color: '#80C398',
    },
    '&:last-of-type': {
      color: '#80C398',
      borderTopRightRadius: '5px',
      borderBottomRightRadius: '5px',
    },
  },
  scoreRange: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: '.5em',
    fontWeight: 600,
  },
  textBox: {
    background: 'none',
    border: '1px solid #ccc',
    borderRadius: '5px',
    minHeight: '100px',
    padding: '15px',
    '&:focus-visible': {
      outline: '1px solid #0098C7',
    },
    color: theme.palette.mode === 'light' ? 'rgb(66, 66, 66)' : 'white',
  },
  submitScoreButton: {
    margin: '2em 0',
    padding: '7px 35px',
    justifyContent: 'left',
    width: 'fit-content',
  },
}));

const KIXIE_ICON_IMG = toAbsoluteUrl('/media/logos/Kixie-Logo-Bubble-Red.png');

export function NPSModal({ show, handleClose }) {
  const classes = useStyles();
  const scores = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const [userAtom] = useRecoilStateLoadable(user);
  const [score, setScore] = React.useState(-1);
  const [comment, setComment] = React.useState('');
  const handleScore = (e, newScore) => {
    setScore(newScore);
  };
  const handleComment = (e) => {
    setComment(e.target.value);
  };
  const addNpsSchema = Yup.object().shape({
    score: Yup.number().required('Required'),
    comment: Yup.string().optional(),
  });
  const [
    addNpsScore,
    // addNpsScoreResult,
  ] = useAddNpsScoreMutation();
  return (
    <Formik
      style={{ borderRadius: '12px' }}
      initialValues={{
        businessid:
          userAtom.state === 'hasValue'
            ? (userAtom.contents as IUserAtom).businessid ?? ''
            : '',
        userid:
          userAtom.state === 'hasValue'
            ? (userAtom.contents as IUserAtom).userid ?? ''
            : '',
        score: -1,
        comment: '',
      }}
      onSubmit={async (values, actions) => {
        if (!userAtom.contents || !('userid' in userAtom.contents)) {
          throw new Error('userAtom is empty');
        }
        addNpsScore({
          userSettings: {
            userid: userAtom.contents.userid,
            userId: userAtom.contents.userid,
            businessid: userAtom.contents.businessid,
            businessId: userAtom.contents.businessid,
            apikey: userAtom.contents.apikey,
          },
          score: score,
          comment: comment,
        });
        handleClose();
      }}
      validationSchema={addNpsSchema}
    >
      {(formik) => (
        <ModalWithStyles
          className={classes.npsModal}
          show={show}
          hideFooter={true}
          hideHeader={true}
          nonScroll={true}
          title="NPS Score"
          onHide={() => {
            formik.resetForm({});
            handleClose();
          }}
          onSubmit={formik.handleSubmit}
          size={'lg'}
        >
          <div className={classes.closeButtonContainer}>
            <div style={{ width: '43.5px' }} />
            <div className={classes.kixieIconContainer}>
              <img
                alt="Kixie Icon"
                style={{ height: '80px', width: '80px' }}
                src={KIXIE_ICON_IMG}
              />
            </div>
            <IconButton onClick={handleClose} size="large">
              <CloseIcon fontSize="inherit" className={classes.closeButton} />
            </IconButton>
          </div>
          <div className={classes.modalContainer}>
            <div className={classes.centerDisplay}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div className={clsx(classes.title, classes.centerDisplay)}>
                  Rate your experience!
                </div>
                <div className={clsx(classes.subtitle)}>
                  How likely is it that you would recommend Kixie to a friend or
                  colleague?
                </div>
                <ToggleButtonGroup
                  exclusive
                  id="score"
                  className={clsx(classes.scoreButtons)}
                  onChange={handleScore}
                  value={score}
                >
                  {scores.map((score) => (
                    <ToggleButton
                      className={clsx(classes.score)}
                      key={score}
                      value={score}
                    >
                      {score}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <div className={clsx(classes.scoreRange)}>
                  <span style={{ color: '#f10606', fontSize: 14 }}>
                    Not likely
                  </span>
                  <span style={{ color: '#80C398', fontSize: 14 }}>
                    Highly likely
                  </span>
                </div>
                <br />
                <div className={clsx(classes.subtitle)}>
                  Any other feedback?
                </div>
                <TextareaAutosize
                  id="comment"
                  name="comment"
                  className={clsx(classes.textBox)}
                  placeholder="Type here"
                  onChange={(e) => handleComment(e)}
                  value={comment}
                  maxLength={500}
                />
                <Button
                  className={clsx(classes.submitScoreButton)}
                  variant="contained"
                  color="primary"
                  data-pup="nps-score"
                  disabled={formik.isSubmitting || score === -1}
                  onClick={() => {
                    formik.handleSubmit();
                  }}
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>
        </ModalWithStyles>
      )}
    </Formik>
  );
}
